<template>
  <div class="w-100 h-100">
    <v-divider />
    <StoreAllocationTable
      id="store-allocation-table"
      ref="store-allocation-table"
      mode="scenario-cluster"
      @has-selected-rows="$emit('has-selected-rows', $event)"
      @data-changed="$emit('data-changed', $event)"
      @on-save="onSave()"
      @open-cluster-generation-modal="openClusterGenerationModal($event)"
    />

    <DependencyTreeFeedbackModal
      :value="isDependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      page="clustering"
      @close="closeDependencyTreeModal"
      @commit="onSave(true)"
    />

    <ClusterGenerationModal
      v-if="isClusterGenerationModalOpen"
      v-model="isClusterGenerationModalOpen"
      :sub-cluster-from="clusterToSubCluster"
      @process="processNewSubClusteringSchemes"
      @close="closeClusterGenerationModal"
    />
  </div>
</template>

<script>
import { useClusteringStore } from '@/stores/clustering';
import { useContextStore } from '@/stores/context';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useScenariosStore } from '@/stores/scenarios';
import { find, isEqual } from 'lodash';
import { mapActions, mapState } from 'pinia';

export default {
  emits: ['has-selected-rows', 'data-changed'],
  data() {
    return {
      dependencyTreeFeedback: {},
      isDependencyTreeModalOpen: false,
      isClusterGenerationModalOpen: false,
      clusterToSubCluster: null,
    };
  },

  computed: {
    ...mapState(useClusteringStore, ['selectedScheme']),
    ...mapState(useScenariosStore, ['stores']),
    ...mapState(useContextStore, ['getDateFormats', 'getClientConfig']),
  },

  methods: {
    ...mapActions(useClusteringStore, ['fetchScenarioClusters', 'saveClusteringSchemes']),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useScenariosStore, ['refreshScenario']),

    moveRowsToGroup({ groupClusterName }) {
      this.$refs['store-allocation-table'].moveRowsToGroup({ groupClusterName });
    },

    closeDependencyTreeModal() {
      this.isDependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    openClusterGenerationModal(params) {
      this.clusterToSubCluster = find(
        this.selectedScheme.clusters,
        ({ clusterName }) => isEqual(clusterName, params.node.key) // set sub-clustering source
      );
      this.isClusterGenerationModalOpen = true;
    },

    closeClusterGenerationModal() {
      this.isClusterGenerationModalOpen = false;
      this.clusterToSubCluster = null; // reset sub-clustering source
    },

    async processNewSubClusteringSchemes(body) {
      await this.saveClusteringSchemes(body);
      await this.fetchScenarioClusters();
      await this.refreshScenario();
    },

    async onSave(commit = false) {
      // Perform dependency tree check if scheme has run switching
      if (this.selectedScheme.selected) {
        const results = await this.triggerDependencyTree({
          params: {
            change: 'clusteringModified',
            updates: {
              global: {
                scenarioClusterSchemeId: this.selectedScheme._id,
                clusters: this.$refs['store-allocation-table'].formatClusterData(),
              },
            },
            commit,
          },
        });

        if (results.needsFeedback) {
          this.dependencyTreeFeedback = results.output;
          this.isDependencyTreeModalOpen = true;
          return;
        }
      }
      // scheme is not selected or DT feedback is not needed -> proceed with save
      await this.$refs['store-allocation-table'].onSaveRefresh();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.ag-row-group) {
  background: $assortment-table-white-bg-colour;
}

:deep(.ag-row-selected),
:deep(.ag-row-selected .ag-row-group) {
  background: $assortment-table-blue-bg-colour !important;

  &:before {
    content: none;
  }
}

:deep(.sub-clustering-icon) {
  i {
    transform: rotate(90deg) scale(90%);
    color: $assortment-sub-clustering-button-background-color;
  }
}

.clickable {
  &:hover {
    cursor: pointer;
  }
}
</style>
